import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import MultiSelectChips from "../components/MultiSelectChips";

const EMPTY_OPTIONS = [];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [catalog, setCatalog] = useState({ countries: [], industries: [], functions: [] });
  const [form, setForm] = useState({
    name: "", company: "", email: "", password: "",
    biography: "", expertise: [], countries: [], industries: [], functions: [],
    whatsapp: "", booking_link: "", profile_photo: "",
    invite_code: "",
  });
  const [invitePreview, setInvitePreview] = useState(null);  // {valid, plan, note} or null
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.countries.length === 0 || form.industries.length === 0 || form.functions.length === 0) {
      toast.error("Select at least one country, industry, and function");
      return;
    }
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Account created! Your Growth Campaign™ link is bizpulseai.net/c/${u.slug}`, { duration: 8000 });
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <div className="overline"><span className="accent-line" /> Consultant registration</div>
      <h1 className="font-display text-4xl mt-3">Create your BizPulse profile</h1>
      <p className="text-muted-foreground mt-2">Set up your expertise and get a unique Growth Campaign™ link in under 5 minutes.</p>

      <form className="mt-10 space-y-7" onSubmit={submit}>
        <Card className="card-elevated p-7 grid md:grid-cols-2 gap-5">
          <Field label="Full name" required><Input data-testid="reg-name" value={form.name} onChange={set("name")} required /></Field>
          <Field label="Company"><Input data-testid="reg-company" value={form.company} onChange={set("company")} /></Field>
          <Field label="Email" required><Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required /></Field>
          <Field label="Password" required><Input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required minLength={6} /></Field>
          <Field label="WhatsApp number (with country code)"><Input data-testid="reg-whatsapp" placeholder="+966 55 555 5555" value={form.whatsapp} onChange={set("whatsapp")} /></Field>
          <Field label="Booking link"><Input data-testid="reg-booking" placeholder="https://cal.com/yourname" value={form.booking_link} onChange={set("booking_link")} /></Field>
          <Field label="Profile photo URL"><Input data-testid="reg-photo" placeholder="https://..." value={form.profile_photo} onChange={set("profile_photo")} /></Field>
          <div className="md:col-span-2 border-t border-border/40 pt-5">
            <Field label="Beta invite code (optional)">
              <div className="flex items-center gap-2">
                <Input data-testid="reg-invite" placeholder="BETA-XXXXXX" value={form.invite_code}
                  onChange={(e) => setForm({...form, invite_code: e.target.value.toUpperCase()})}
                  onBlur={async () => {
                    const code = (form.invite_code || "").trim();
                    if (!code) { setInvitePreview(null); return; }
                    try {
                      const { data } = await api.get("/invites/preview", { params: { code } });
                      setInvitePreview(data);
                    } catch { setInvitePreview({ valid: false, error: "Could not verify code" }); }
                  }}
                />
                {invitePreview && (
                  invitePreview.valid
                    ? <span className="text-xs text-emerald-300" data-testid="reg-invite-ok">✓ {invitePreview.plan} plan unlocked</span>
                    : <span className="text-xs text-red-300" data-testid="reg-invite-err">{invitePreview.error || "Invalid"}</span>
                )}
              </div>
            </Field>
          </div>
        </Card>

        <Card className="card-elevated p-7 space-y-5">
          <Field label="Biography"><Textarea data-testid="reg-bio" rows={4} value={form.biography} onChange={set("biography")} placeholder="Your background, sectors, and notable engagements." /></Field>
          <Field label="Areas of expertise (free tags)">
            <MultiSelectChips testid="reg-expertise" value={form.expertise} onChange={set("expertise")} options={EMPTY_OPTIONS} allowFree />
          </Field>
          <Field label="Countries served">
            <MultiSelectChips testid="reg-countries" value={form.countries} onChange={set("countries")} options={catalog.countries} />
          </Field>
          <Field label="Industries served">
            <MultiSelectChips testid="reg-industries" value={form.industries} onChange={set("industries")} options={catalog.industries} />
          </Field>
          <Field label="Business functions">
            <MultiSelectChips testid="reg-functions" value={form.functions} onChange={set("functions")} options={catalog.functions} />
          </Field>
        </Card>

        <div className="flex items-center justify-between">
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="reg-login-link">Already have an account? Sign in</Link>
          <Button data-testid="reg-submit" disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-7">
            {busy ? "Creating…" : "Create my account"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}{required && <span className="text-accent"> *</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
